import { FC } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { helpNavigationSearch } from "@/config/helpNavigationContext";

type LanguageSwitcherProps = {
  /**
   * Icon-only. The desktop header carries four nav items plus log-in plus
   * Boka demo, and a 126px-wide "Svenska ⌄" control is the least informative
   * of them — the globe is understood and buys ~80px back for the CTAs.
   *
   * Still the same native `<select>`, kept keyboard- and screen-reader-
   * operable: it is laid over the icon at `opacity-0` rather than replaced by
   * a custom widget, so `aria-label`, focus and the OS picker all behave as
   * before. The labelled control stays the default and is what the mobile
   * sheet uses, where the space exists and the words help.
   */
  compact?: boolean;
};

export const LanguageSwitcher: FC<LanguageSwitcherProps> = ({
  compact = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation(["common", "navigation"]);
  const { language, getAlternateLink } = useLanguage();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lang = e.target.value as "sv" | "en";
    console.log(`Switching language from ${language} to ${lang}`);

    // Only navigate if language is actually changing
    if (lang !== language) {
      const target = `${getAlternateLink(location.pathname)}${helpNavigationSearch(location.pathname, location.search)}`;
      console.log(`Navigating to ${target}`);

      // Log available languages and namespaces for debugging
      console.log("[i18n] Available languages:", i18n.languages);
      console.log(
        "[i18n] Available namespaces:",
        i18n.reportNamespaces?.getUsedNamespaces?.(),
      );
      console.log(
        "[i18n] Current resource store:",
        i18n.getResourceBundle?.(lang, "navigation"),
      );

      // Change language first, then navigate
      i18n.changeLanguage(lang).then(() => {
        navigate(target);
      });
    }
  };

  const options = (
    <>
      <option value="sv" className="bg-white text-caire-ink">
        Svenska
      </option>
      <option value="en" className="bg-white text-caire-ink">
        English
      </option>
    </>
  );

  if (compact) {
    return (
      <div className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-hairline-strong bg-white text-slate transition-colors hover:border-coral-deep focus-within:ring-2 focus-within:ring-coral/40">
        <Languages
          aria-hidden="true"
          data-testid="language-switcher-icon"
          className="pointer-events-none h-4 w-4"
        />
        <select
          aria-label="Select language"
          value={language}
          onChange={handleChange}
          className="absolute inset-0 cursor-pointer opacity-0"
          style={{ colorScheme: "light" }}
        >
          {options}
        </select>
      </div>
    );
  }

  return (
    <div className="relative inline-flex items-center">
      <Languages
        aria-hidden="true"
        data-testid="language-switcher-icon"
        className="pointer-events-none absolute left-2.5 h-4 w-4 text-slate"
      />
      <select
        aria-label="Select language"
        value={language}
        onChange={handleChange}
        className="rounded-md border border-hairline-strong bg-white py-1 pl-8 pr-2 text-caire-ink transition-colors hover:border-coral-deep focus:outline-none focus:ring-2 focus:ring-coral/40"
        style={{
          colorScheme: "light",
        }}
      >
        {options}
      </select>
    </div>
  );
};
